import React from 'react'
import { Box, Button, CircularProgress, Paper, Typography } from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'

import type { Figure, VerdictTone } from '../reconciliationModel'

/**
 * The verdict strip (design/2026-10-19-sky-reconciliation-page-redesign.md,
 * M1): the one bold element on the page. A plain sentence, then three figures:
 * comments and posts, Sprinklr beside Platinum with the difference and percent,
 * and what Platinum adds on top. Under it, where and when Sprinklr was read.
 *
 * Posts' Platinum figure here is Platinum on Sky's terms (the dashboards'
 * number with the agreed adjustments made); the working shows the dashboards'
 * own number, labelled as such. The two are never on screen unlabelled.
 */
export interface VerdictStripProps {
  verdict: { tone: VerdictTone; text: string }
  comments: Figure
  /** Under the comments figure: what Sprinklr holds after Platinum's last import. */
  commentsNote?: string
  posts: Figure
  /** Coded comments, and X's counted-but-not-coded figure. */
  added: { coded: string; x: string; hasX: boolean }
  provenance: string
  partial?: boolean
  /** A Manager may skip the hour Sky's answer is kept. */
  canReadAgain?: boolean
  onReadAgain?: () => void
  readingAgain?: boolean
  busy?: boolean
}

const TONE_ICON = { success: CheckCircleOutlineIcon, info: InfoOutlinedIcon, error: ErrorOutlineIcon } as const

const num = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' } as const

const FigureBlock: React.FC<{ title: string; platinumLabel: string; f: Figure; testId: string; note?: string }> = ({ title, platinumLabel, f, testId, note }) => (
  <Box
    data-testid={testId}
    sx={{
      display: { xs: 'flex', md: 'block' },
      justifyContent: 'space-between',
      alignItems: 'baseline',
      gap: 2,
      py: { xs: 1, md: 0 },
      borderTop: { xs: 1, md: 0 },
      borderColor: 'divider',
    }}
  >
    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: { md: 0.5 } }}>{title}</Typography>
    <Box sx={{ textAlign: { xs: 'right', md: 'left' } }}>
      <Typography sx={{ ...num, typography: { xs: 'body1', md: 'h6' }, lineHeight: 1.35 }}>Sprinklr {f.sprinklr}</Typography>
      <Typography sx={{ ...num, typography: { xs: 'body1', md: 'h6' }, lineHeight: 1.35 }}>
        {platinumLabel} {f.platinum}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={num}>
        {f.same ? 'Same' : `${f.diff} (${f.pct})`}
      </Typography>
      {note && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, maxWidth: '36ch' }}>
          {note}
        </Typography>
      )}
    </Box>
  </Box>
)

const VerdictStrip: React.FC<VerdictStripProps> = ({
  verdict, comments, commentsNote, posts, added, provenance, partial, canReadAgain, onReadAgain, readingAgain, busy,
}) => {
  const Icon = TONE_ICON[verdict.tone]
  return (
    <Paper
      variant="outlined"
      data-testid="recon-headline"
      sx={{ p: { xs: 2, sm: 3 }, borderLeft: 4, borderLeftColor: `${verdict.tone}.main` }}
    >
      <Box data-testid="recon-verdict" data-tone={verdict.tone} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
        <Icon sx={{ color: `${verdict.tone}.main`, mt: 0.25, fontSize: 28 }} aria-hidden />
        <Typography variant="h5" component="p" sx={{ fontWeight: 600, maxWidth: '60ch', lineHeight: 1.35 }}>
          {verdict.text}
        </Typography>
      </Box>

      <Box
        sx={{
          mt: { xs: 2, md: 3 },
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1.2fr' },
          columnGap: 4,
        }}
      >
        <FigureBlock title="Comments" platinumLabel="Platinum" f={comments} note={commentsNote} testId="recon-figure-comments" />
        <FigureBlock title="Posts" platinumLabel="Platinum (on Sky's terms)" f={posts} testId="recon-figure-posts" />
        <Box
          data-testid="recon-figure-added"
          sx={{ py: { xs: 1, md: 0 }, borderTop: { xs: 1, md: 0 }, borderColor: 'divider' }}
        >
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: { md: 0.5 } }}>Added by Platinum</Typography>
          <Typography sx={{ typography: { xs: 'body1', md: 'h6' }, lineHeight: 1.35 }}>
            <Box component="span" sx={num}>{added.coded}</Box> comments coded for themes, sentiment and emotion
          </Typography>
          {added.hasX && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              X: <Box component="span" sx={num}>{added.x}</Box> counted, none coded
            </Typography>
          )}
        </Box>
      </Box>

      <Box
        data-testid="recon-provenance"
        sx={{ mt: 2.5, pt: 1.5, borderTop: 1, borderColor: 'divider', display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 1, rowGap: 0.5 }}
      >
        <Typography variant="body2" color="text.secondary">
          {provenance}
          {partial && ' Showing the departments you can open.'}
          {canReadAgain && ' Kept for an hour.'}
        </Typography>
        {canReadAgain && (
          <Button
            size="small"
            color="info"
            onClick={onReadAgain}
            disabled={busy || readingAgain}
            startIcon={readingAgain ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            Read Sprinklr again
          </Button>
        )}
      </Box>
    </Paper>
  )
}

export default VerdictStrip
